import { ImmutableVector3 } from '@modugen/ui-toolkit'

/** Maximum distance (m) a wall end may be extended or shortened to reach another wall. */
export const WALL_CONNECTION_THRESHOLD = 0.3

/** Distance (m) below which a wall end counts as already connected and stays untouched. */
const CONNECTION_EPSILON = 0.01

/** Maximum perpendicular offset (m) for treating parallel walls as collinear. */
const COLLINEAR_TOLERANCE = 0.02

/** Guid prefix of proposal walls, so they never collide with the real model elements. */
export const WALL_CONNECTION_PROPOSAL_PREFIX = 'wall-connection-proposal-'

/** Coordinate difference (m) below which a wall already matches the proposal geometry. */
const PROPOSAL_MATCH_EPSILON = 0.001

interface Point2 {
  x: number
  y: number
}

interface Segment2 {
  start: Point2
  end: Point2
}

const cross = (a: Point2, b: Point2): number => a.x * b.y - a.y * b.x
const dot = (a: Point2, b: Point2): number => a.x * b.x + a.y * b.y
const subtract = (a: Point2, b: Point2): Point2 => ({ x: a.x - b.x, y: a.y - b.y })

const distanceToSegment = (p: Point2, { start, end }: Segment2): number => {
  const ab = subtract(end, start)
  const lengthSq = dot(ab, ab)
  const t = lengthSq === 0 ? 0 : Math.min(1, Math.max(0, dot(subtract(p, start), ab) / lengthSq))
  return Math.hypot(start.x + ab.x * t - p.x, start.y + ab.y * t - p.y)
}

/**
 * Signed distance the wall end `p` has to be moved along `direction` (unit vector pointing out of
 * the wall) to meet `target` — positive extends the wall, negative shortens it. `undefined` when
 * the target cannot be met (parallel but not collinear, or the meeting point lies too far outside
 * the target wall).
 */
const connectionOffsetToSegment = (
  p: Point2,
  direction: Point2,
  target: Segment2,
): number | undefined => {
  const ab = subtract(target.end, target.start)
  const targetLength = Math.hypot(ab.x, ab.y)
  if (targetLength === 0) return undefined

  const denominator = cross(direction, ab)

  // Parallel walls: only collinear ones can be met, by extending up to the nearer target end.
  if (Math.abs(denominator) < 1e-9) {
    const perpendicular = Math.abs(cross(ab, subtract(p, target.start))) / targetLength
    if (perpendicular > COLLINEAR_TOLERANCE) return undefined
    const extensions = [
      dot(subtract(target.start, p), direction),
      dot(subtract(target.end, p), direction),
    ].filter(t => t > 0)
    return extensions.length === 0 ? undefined : Math.min(...extensions)
  }

  const ap = subtract(target.start, p)
  const t = cross(ap, ab) / denominator
  const s = cross(ap, direction) / denominator

  // The meeting point must lie on the target wall or within the threshold beyond its ends (so
  // L-corners where both walls stop short of the corner are still found).
  const alongTarget = s * targetLength
  if (
    alongTarget < -WALL_CONNECTION_THRESHOLD ||
    alongTarget > targetLength + WALL_CONNECTION_THRESHOLD
  ) {
    return undefined
  }

  return t
}

/**
 * Offset (m) to move the wall end `p` along the wall axis so it connects to one of `targets`
 * (positive = extend, negative = shorten; the target needing the smallest move wins). `undefined`
 * when the end is already connected, no target is reachable within the threshold, or shortening
 * would consume the whole wall.
 */
const connectionOffsetForEnd = (
  p: Point2,
  otherEnd: Point2,
  targets: Segment2[],
): number | undefined => {
  const axis = subtract(p, otherEnd)
  const wallLength = Math.hypot(axis.x, axis.y)
  if (wallLength === 0) return undefined
  const direction = { x: axis.x / wallLength, y: axis.y / wallLength }

  // Ends already touching another wall stay untouched.
  if (targets.some(target => distanceToSegment(p, target) <= CONNECTION_EPSILON)) return undefined

  let best: number | undefined
  targets.forEach(target => {
    const offset = connectionOffsetToSegment(p, direction, target)
    if (offset === undefined || Math.abs(offset) > WALL_CONNECTION_THRESHOLD) return
    if (Math.abs(offset) <= CONNECTION_EPSILON) return
    // Shortening must leave a wall behind.
    if (offset < 0 && wallLength + offset <= CONNECTION_EPSILON) return
    if (best === undefined || Math.abs(offset) < Math.abs(best)) best = offset
  })
  return best
}

const movedPoint = (point: Point, otherEnd: Point, offset: number | undefined): Point => {
  if (offset === undefined) return point
  const axis = { x: point.x - otherEnd.x, y: point.y - otherEnd.y }
  const length = Math.hypot(axis.x, axis.y)
  return new ImmutableVector3(
    point.x + (axis.x / length) * offset,
    point.y + (axis.y / length) * offset,
    point.z,
  )
}

/** Guid of the model wall a proposal wall was derived from. */
export const proposalSourceGuid = (proposalGuid: string): string =>
  proposalGuid.slice(WALL_CONNECTION_PROPOSAL_PREFIX.length)

/**
 * Whether two wall axis points are the same position (within a millimetre) — applying the proposal
 * only sends update requests for wall ends that actually moved.
 */
export const pointsMatch = (a: Point2, b: Point2): boolean =>
  Math.abs(a.x - b.x) <= PROPOSAL_MATCH_EPSILON && Math.abs(a.y - b.y) <= PROPOSAL_MATCH_EPSILON

/**
 * Proposes connections for walls whose ends stop short of (or reach past) a neighbouring wall:
 * each wall end that is not already touching another wall is extended or shortened along the wall
 * axis — by at most `WALL_CONNECTION_THRESHOLD` — to the nearest meeting point with another wall.
 * Returns only the walls an end was moved for, as copies with prefixed guids and adjusted points.
 */
export const computeWallConnectionProposals = <T extends ShapeObject>(walls: T[]): T[] => {
  const segmentByGuid = new Map<string, Segment2>()
  walls.forEach(wall => {
    const [p1, p2] = wall.shape.points
    if (!p1 || !p2) return
    segmentByGuid.set(wall.guid, { start: { x: p1.x, y: p1.y }, end: { x: p2.x, y: p2.y } })
  })

  const proposals: T[] = []
  walls.forEach(wall => {
    const segment = segmentByGuid.get(wall.guid)
    if (!segment) return

    const targets = walls
      .filter(other => other.guid !== wall.guid)
      .map(other => segmentByGuid.get(other.guid))
      .filter((target): target is Segment2 => !!target)

    const startOffset = connectionOffsetForEnd(segment.start, segment.end, targets)
    const endOffset = connectionOffsetForEnd(segment.end, segment.start, targets)
    if (startOffset === undefined && endOffset === undefined) return

    const [p1, p2, ...rest] = wall.shape.points
    proposals.push({
      ...wall,
      guid: `${WALL_CONNECTION_PROPOSAL_PREFIX}${wall.guid}`,
      shape: {
        ...wall.shape,
        points: [movedPoint(p1, p2, startOffset), movedPoint(p2, p1, endOffset), ...rest],
      },
    })
  })
  return proposals
}
